/**
 * ТЗ 0090 — окно «Поставить на лесенку»: какие варианты в нём есть и что
 * каждый сделает, ДО нажатия.
 *
 * Лесенок три:
 * - «Подпись договора» (`contract_sign`, тема `esign_signature`) — повод —
 *   договор студента, шаг уходит один раз; перезапуска с нуля нет;
 * - «Напоминания» (`reminder`) и «Анкета» (`dofin_form`) — запуск лида
 *   (`startLeadLadder`), как в 0084.
 *
 * Окно одно на карточку лида и карточку договора; разница — в том, что
 * можно сделать с договором-черновиком: из лида его только открыть, из
 * договора — выдать ссылку (`sendContract(sendEmail:false)`, то же, что
 * «Скопировать ссылку»): договор станет «отправлен», робот поставит его на
 * лесенку сам.
 *
 * Модуль без React, Apollo и i18n: пояснения — ключи, их переводит окно.
 */

import { LEAD_LADDER_KINDS, LeadLadderKind } from './leadsModel';

/** Варианты окна — в порядке показа. */
export const LADDER_PICK_IDS = [
  'contract_sign',
  'reminder',
  'dofin_form',
] as const;
export type LadderPickId = typeof LADDER_PICK_IDS[number];

/** Вариант подписи договора (не лесенка лида). */
export const CONTRACT_SIGN_PICK: LadderPickId = 'contract_sign';

export const isLeadLadderKind = (id: string): id is LeadLadderKind =>
  (LEAD_LADDER_KINDS as readonly string[]).includes(id);

/** Что сделает кнопка окна у выбранного варианта. */
export const LADDER_PICK_ACTIONS = [
  'start',
  'resume',
  'issue_link',
  'open_contract',
  'create_contract',
  'none',
] as const;
export type LadderPickAction = typeof LADDER_PICK_ACTIONS[number];

/** Где открыто окно. */
export type LadderPickWhere = 'lead' | 'contract';

/**
 * Состояние лесенки подписи (`LeadContractSigningLadderType.state`):
 * черновик или отправка не удалась — ссылки ещё нет; отправляется; идёт или
 * остановлена; закрыта (подписан, отклонён, отменён, истёк, ссылка мертва).
 */
export const SIGNING_LADDER_STATES = [
  'not_sent',
  'sending',
  'running',
  'stopped',
  'closed',
] as const;
export type SigningLadderState = typeof SIGNING_LADDER_STATES[number];

/**
 * Пояснения вариантов (`leads.ladders.contract.notes.<code>`). Каждый код
 * — в словаре: локаль-гейт перечисляет семейство отсюда.
 */
export const LADDER_PICK_NOTES = [
  'noContract',
  'esignOff',
  'companyContract',
  'draftFromLead',
  'issueLink',
  'sending',
  'running',
  'stepsOver',
  'stopped',
  'closedSigned',
  'closedExpired',
  'closedCancelled',
  'closedRejected',
  'closed',
  'handledBy',
  'handledByTeam',
  'smsUnavailable',
  'autoOff',
  'noLead',
  'leadClosed',
] as const;
export type LadderPickNote = typeof LADDER_PICK_NOTES[number];

/** Ключ пояснения; `noLead` и `leadClosed` — общие слова раздела. */
export const ladderPickNoteKey = (note: LadderPickNote): string => {
  if (note === 'noLead') return 'leads.ladders.noLead';
  if (note === 'leadClosed') return 'leads.ladders.closedHint';
  return `leads.ladders.contract.notes.${note}`;
};

/** Состояние лесенки подписи — подписью (`leads.ladders.contract.states`). */
export const signingStateKey = (state: string | null | undefined) =>
  state && (SIGNING_LADDER_STATES as readonly string[]).includes(state)
    ? `leads.ladders.contract.states.${state}`
    : null;

type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

/**
 * Тон чипа состояния: идёт — «инфо» (или «внимание», если SMS сейчас не
 * уходят), остановлена — «внимание», закрыта по подписи — «ок», прочее —
 * нейтрально.
 */
export const signingStateTone = (
  ladder: Pick<ISigningLadder, 'state' | 'available'> | null | undefined,
  contractStatus?: string | null,
): Tone => {
  switch (ladder?.state) {
    case 'running':
      return ladder.available ? 'info' : 'warn';
    case 'stopped':
      return 'warn';
    case 'closed':
      return contractStatus === 'completed' ? 'ok' : 'neutral';
    default:
      return 'neutral';
  }
};

/** Лесенка подписи договора студента — поля `signingLadder`. */
export interface ISigningLadder {
  state: string;
  available: boolean;
  reason?: string | null;
  autoReminders: boolean;
  nextStepAt?: string | null;
  nextStepReason?: string | null;
  sentAt?: string | null;
  canManage: boolean;
  handlerName?: string | null;
}

/** Договор — цель варианта «Подпись договора». */
export interface ILadderPickContract {
  id: string;
  number?: string | null;
  kind: string;
  status: string;
  createdAt?: string | null;
  signingLadder?: ISigningLadder | null;
}

const STUDENT_KIND = 'student';

/**
 * По какому договору лида идёт (или пойдёт) лесенка подписи: последний
 * отправленный или открытый, иначе последний отправляемый, иначе последний
 * черновик (или «отправка не удалась»), иначе последний вообще. Только
 * договоры студента: у фирм и преподавателей лесенки подписи нет.
 *
 * Отправляемый — выше черновика: он уже в пути, и старый черновик рядом с
 * ним — не то, о чём спрашивают.
 */
const SIGNING_TIERS: ReadonlyArray<ReadonlyArray<string>> = [
  ['sent', 'opened'],
  ['sending'],
  ['draft', 'send_failed'],
];

const newestFirst = <T extends { id: string; createdAt?: string | null }>(
  a: T,
  b: T,
): number => {
  const byDate = (b.createdAt || '').localeCompare(a.createdAt || '');
  if (byDate !== 0) return byDate;
  return Number(b.id) - Number(a.id);
};

export const pickSigningContract = <
  T extends {
    id: string;
    kind: string;
    status: string;
    createdAt?: string | null;
  }
>(
  contracts: ReadonlyArray<T> | null | undefined,
): T | null => {
  const students = (contracts ?? [])
    .filter((row) => row.kind === STUDENT_KIND)
    .sort(newestFirst);
  if (students.length === 0) return null;
  const tier = SIGNING_TIERS.map((statuses) =>
    students.find((row) => statuses.includes(row.status)),
  ).find(Boolean);
  return tier ?? students[0];
};

/** Кнопки строки «Подпись договора» в карточке лида. */
export const signingRowActions = (
  ladder: ISigningLadder | null | undefined,
): { stop: boolean; resume: boolean } => ({
  stop: Boolean(ladder?.canManage) && ladder?.state === 'running',
  resume: Boolean(ladder?.canManage) && ladder?.state === 'stopped',
});

/** Закрытая лесенка подписи — пояснение по статусу договора. */
const closedNote = (status: string): LadderPickNote => {
  switch (status) {
    case 'completed':
      return 'closedSigned';
    case 'expired':
      return 'closedExpired';
    case 'cancelled':
      return 'closedCancelled';
    case 'rejected':
      return 'closedRejected';
    default:
      return 'closed';
  }
};

/**
 * Лесенка подписи договора в карточке ДОГОВОРА — из самого договора и его
 * напоминаний (`contractOutreach`), теми же правилами, что сервер считает
 * `signingLadder` для карточки лида (`crm/services/lead_contracts.py`).
 *
 * Напоминания читаются только у отправленного договора и только тем, кто
 * ведёт переписку; нет ответа — состояние по статусу, без причины.
 */
export const contractSigningLadder = (
  contract: {
    kind: string;
    status: string;
    cancelConflict?: boolean | null;
    canManage: boolean;
    sellerName?: string | null;
    sentAt?: string | null;
  },
  outreach: {
    available: boolean;
    reason?: string | null;
    stopped: boolean;
    autoReminders: boolean;
    nextStepAt?: string | null;
    nextStepReason?: string | null;
  } | null,
): ISigningLadder | null => {
  if (contract.kind !== STUDENT_KIND) return null;
  const base: ISigningLadder = {
    state: 'closed',
    available: false,
    reason: 'closed',
    autoReminders: false,
    nextStepAt: null,
    nextStepReason: null,
    sentAt: contract.sentAt ?? null,
    canManage: contract.canManage,
    handlerName: contract.canManage ? null : contract.sellerName || null,
  };
  const { status } = contract;
  if (status === 'draft' || status === 'send_failed' || status === 'sending') {
    // До отправки напоминаний не читаем: канал и телефон проверит окно
    // карточки лида; здесь — «не знаем», а не «нельзя».
    return {
      ...base,
      state: status === 'sending' ? 'sending' : 'not_sent',
      available: true,
      reason: null,
      autoReminders: true,
    };
  }
  const waiting = status === 'sent' || status === 'opened';
  if (!waiting || contract.cancelConflict) return base;
  if (!outreach) {
    return { ...base, state: 'running', available: true, reason: null };
  }
  if (!outreach.available && outreach.reason === 'closed') return base;
  return {
    ...base,
    state: outreach.stopped ? 'stopped' : 'running',
    available: outreach.available,
    reason: outreach.reason ?? null,
    autoReminders: outreach.autoReminders,
    nextStepAt: outreach.nextStepAt ?? null,
    nextStepReason: outreach.nextStepReason ?? null,
  };
};

/** Данные пояснения — сырые: переводит и форматирует окно. */
export interface ILadderPickParams {
  /** Код причины движка SMS (`esign.outreach.reasons`). */
  reason?: string | null;
  /** Когда следующее SMS (ISO). */
  when?: string | null;
  /** Кто ведёт договор. */
  name?: string | null;
  /** Номер договора. */
  number?: string | null;
}

export interface ILadderPickOption {
  id: LadderPickId;
  enabled: boolean;
  action: LadderPickAction;
  /** Пояснение до нажатия; `null` — хватает описания вида. */
  note: LadderPickNote | null;
  params: ILadderPickParams;
  /**
   * Запустить можно, но SMS сейчас не уйдут: у лесенок лида — код
   * `ladderReadiness`, у подписи — код движка SMS (`autoOff` — робот или
   * план выключен).
   */
  warning: string | null;
  /** Договор варианта «Подпись договора». */
  contractId: string | null;
}

/** Код «робот или план подписи выключен» — не код движка, а свой. */
export const SIGNING_AUTO_OFF = 'auto_off';

export interface ILadderPickInput {
  where: LadderPickWhere;
  /**
   * Лид (у договора без лида — `null`): `canStart` — сервер даст новый
   * запуск (`leadCanStartLadder`).
   */
  lead: { canStart: boolean } | null;
  readiness?: ReadonlyArray<{ kind: string; warning?: string | null }> | null;
  /** Договор варианта подписи: из лида — `pickSigningContract`, в договоре — он сам. */
  contract: ILadderPickContract | null;
  /** Может создать договор студента (роль и `ESIGN_ENABLED`). */
  canCreateContract: boolean;
  /** `ESIGN_ENABLED`: без него ссылку не выдать. */
  esignEnabled: boolean;
}

/** Лиду сервер даёт новый запуск: открыт, в воронке, не удалён. */
export const leadCanStartLadder = (
  lead:
    | { isClosed: boolean; inPipeline: boolean; isDeleted: boolean }
    | null
    | undefined,
): boolean =>
  Boolean(lead) &&
  [!lead?.isClosed, lead?.inPipeline, !lead?.isDeleted].every(Boolean);

const option = (
  id: LadderPickId,
  over: Partial<ILadderPickOption>,
): ILadderPickOption => ({
  id,
  enabled: false,
  action: 'none',
  note: null,
  params: {},
  warning: null,
  contractId: null,
  ...over,
});

/** Почему лесенка подписи сейчас не шлёт — код для строки «не пошлёт». */
const signingWarning = (ladder: ISigningLadder): string | null => {
  if (!ladder.available) return ladder.reason || 'closed';
  if (!ladder.autoReminders) return SIGNING_AUTO_OFF;
  return null;
};

/** «Подпись договора» — по таблице спеки 0090. */
const contractSignOption = ({
  where,
  contract,
  canCreateContract,
  esignEnabled,
}: ILadderPickInput): ILadderPickOption => {
  const id: LadderPickId = 'contract_sign';
  if (!contract) {
    if (where === 'contract') return option(id, { note: 'companyContract' });
    return canCreateContract
      ? option(id, {
          enabled: true,
          action: 'create_contract',
          note: 'noContract',
        })
      : option(id, { note: esignEnabled ? 'noContract' : 'esignOff' });
  }
  const contractId = contract.id;
  const number = contract.number || null;
  const ladder = contract.signingLadder;
  if (contract.kind !== STUDENT_KIND || !ladder) {
    return option(id, { note: 'companyContract', contractId });
  }
  if (ladder.state === 'closed') {
    return option(id, {
      note: closedNote(contract.status),
      contractId,
      params: { number },
    });
  }
  if (ladder.state === 'sending') {
    return option(id, { note: 'sending', contractId, params: { number } });
  }
  // Действия — тому, кто ведёт договор (продавец, AM, Admin): остальные
  // видят состояние, но не кнопку.
  if (!ladder.canManage) {
    return option(id, {
      note: ladder.handlerName ? 'handledBy' : 'handledByTeam',
      contractId,
      params: { name: ladder.handlerName || null, number },
    });
  }
  const warning = signingWarning(ladder);
  if (ladder.state === 'not_sent') {
    if (where === 'lead') {
      return option(id, {
        enabled: true,
        action: 'open_contract',
        note: 'draftFromLead',
        contractId,
        params: { number },
        warning,
      });
    }
    if (!esignEnabled) {
      return option(id, { note: 'esignOff', contractId, params: { number } });
    }
    return option(id, {
      enabled: true,
      action: 'issue_link',
      note: 'issueLink',
      contractId,
      params: { number },
      warning,
    });
  }
  // Остановлена — «Возобновить» тому, кто ведёт договор, всегда: у
  // возобновления на сервере нет условий (как у кнопки строки и вкладки
  // «Переписка»). Робот или план выключен, SMS недоступны — не запрет, а
  // предупреждение: лесенка встанет и начнёт слать, когда это исправят.
  if (ladder.state === 'stopped') {
    return option(id, {
      enabled: true,
      action: 'resume',
      note: 'stopped',
      contractId,
      params: { number },
      warning,
    });
  }
  // Идёт. SMS недоступны — причина движка; робот или план выключен —
  // только вручную.
  if (!ladder.available) {
    return option(id, {
      note: 'smsUnavailable',
      contractId,
      params: { reason: ladder.reason || 'closed', number },
    });
  }
  if (!ladder.autoReminders) {
    return option(id, { note: 'autoOff', contractId, params: { number } });
  }
  if (ladder.nextStepAt) {
    return option(id, {
      note: 'running',
      contractId,
      params: { when: ladder.nextStepAt, number },
    });
  }
  if (ladder.nextStepReason) {
    return option(id, {
      note: 'smsUnavailable',
      contractId,
      params: { reason: ladder.nextStepReason, number },
    });
  }
  return option(id, { note: 'stepsOver', contractId, params: { number } });
};

/** «Напоминания» и «Анкета» — запуск лида (0084). */
const leadKindOption = (
  id: LeadLadderKind,
  { lead, readiness }: ILadderPickInput,
): ILadderPickOption => {
  if (!lead) return option(id, { note: 'noLead' });
  if (!lead.canStart) return option(id, { note: 'leadClosed' });
  const warning =
    (readiness ?? []).find((row) => row.kind === id)?.warning || null;
  return option(id, { enabled: true, action: 'start', warning });
};

/**
 * Варианты окна «Поставить на лесенку» в порядке показа. Каждый говорит до
 * нажатия, можно ли и почему нет; кнопка окна — по `action` выбранного.
 */
export const ladderPickOptions = (
  input: ILadderPickInput,
): ILadderPickOption[] =>
  LADDER_PICK_IDS.map((id) =>
    isLeadLadderKind(id)
      ? leadKindOption(id, input)
      : contractSignOption(input),
  );

/**
 * Какой вариант выбрать при открытии: желаемый, если он доступен, иначе
 * первый доступный, иначе желаемый (окно объяснит, почему нельзя).
 */
export const initialLadderPick = (
  options: ReadonlyArray<ILadderPickOption>,
  preferred: LadderPickId,
): LadderPickId => {
  const wanted = options.find((item) => item.id === preferred);
  if (wanted?.enabled) return preferred;
  return options.find((item) => item.enabled)?.id ?? preferred;
};

/**
 * Пойдут ли SMS двух лесенок одному человеку сразу: подпись договора идёт
 * (или её сейчас запустят) и «Напоминания» лида идут (или их запустят).
 */
export const ladderPickOverlap = (
  picked: ILadderPickOption | null | undefined,
  {
    signingRunning,
    reminderRunning,
  }: { signingRunning: boolean; reminderRunning: boolean },
): boolean => {
  if (!picked || !picked.enabled) return false;
  if (picked.id === 'reminder') return signingRunning;
  if (picked.id === 'contract_sign') {
    return (
      reminderRunning &&
      (picked.action === 'resume' || picked.action === 'issue_link')
    );
  }
  return false;
};
